import { useRef, useState, useEffect } from "react";

export default function SignaturePad({ T, label, hint, value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!value);
  const [mode, setMode] = useState(value ? "done" : "pad"); // pad | done

  useEffect(() => {
    if (value && canvasRef.current) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = value;
      setIsEmpty(false);
      setMode("done");
    }
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    setDrawing(true);
    setIsEmpty(false);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    setDrawing(false);
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    setMode("pad");
    onChange("");
  };

  return (
    <div style={{background:T.card,border:`2px solid ${value?T.success:T.border}`,borderRadius:16,overflow:"hidden",transition:"border 0.3s"}}>
      {/* Header */}
      <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,background:T.bg,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:T.text}}>{label} <span style={{color:T.danger}}>*</span></div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>{hint}</div>
        </div>
        {value && (
          <span style={{fontSize:11,fontWeight:700,color:T.success,background:`${T.success}20`,padding:"3px 10px",borderRadius:20}}>✅ Signé</span>
        )}
      </div>

      {/* Zone signature */}
      <div style={{padding:16}}>
        <div style={{position:"relative",background:"#fff",borderRadius:12,border:`1.5px dashed ${value?"#22d3a0":"#cbd5e1"}`,overflow:"hidden"}}>
          <canvas
            ref={canvasRef}
            width={560}
            height={160}
            style={{display:"block",width:"100%",height:160,touchAction:"none",cursor:"crosshair"}}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          {isEmpty && (
            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",pointerEvents:"none",gap:8}}>
              <span style={{fontSize:32,opacity:0.3}}>✍️</span>
              <span style={{fontSize:13,color:"#94a3b8",fontWeight:500}}>Signer ici avec le doigt ou la souris</span>
            </div>
          )}
        </div>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10}}>
          <div style={{fontSize:11,color:T.muted}}>
            {value ? "✅ Signature enregistrée" : "Tracez votre signature dans le cadre blanc"}
          </div>
          <button onClick={clear} style={{padding:"5px 12px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:11,cursor:"pointer",fontWeight:600}}>
            🗑️ Effacer
          </button>
        </div>
      </div>
    </div>
  );
}
